import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { ApiTokenService } from "./api-token.service";
import {
  ElementAPrefacturer,
  MissionsFacturablesResult,
  ResultatPrefacturation,
} from "../../interfaces/mission-facturable";
import { PrefacturationResume } from "../../interfaces/prefacturation";

/** Options de GET prefacturations/missions (FR-185). */
export interface OptionsMissionsFacturables {
  /** Restreint aux missions/compléments restant à préfacturer (vue par défaut). */
  nonPrefacturees?: boolean;
}

/**
 * Communication avec les endpoints « préfacturation » de l'API .NET
 * (module « Ma facturation » : liste des missions facturables).
 */
@Injectable({
  providedIn: "root",
})
export class ApiPrefacturationService {
  private readonly newAPI: string;

  constructor(
    private http: HttpClient,
    private apiTokenService: ApiTokenService
  ) {
    this.newAPI = environment.newAPI;
  }

  /**
   * Missions facturables (Validée / Refusée PDV) de l'intervenant connecté,
   * chargées intégralement. Réponse { items, hasMore } — filtre « non
   * préfacturées » côté serveur (FR-185) ; sans pagination demandée, hasMore
   * est toujours faux.
   */
  async getMissionsFacturables(
    options: OptionsMissionsFacturables = {}
  ): Promise<MissionsFacturablesResult> {
    const headers = await this.apiTokenService.getHeader();

    let params = new HttpParams();
    if (options.nonPrefacturees) {
      params = params.set("nonPrefacturees", true);
    }

    return await lastValueFrom(
      this.http.get<MissionsFacturablesResult>(
        `${this.newAPI}prefacturations/missions`,
        { headers, params }
      )
    );
  }

  /**
   * Préfacturations de l'intervenant connecté (page « Mes préfacturations »).
   * `annee` filtre côté serveur sur l'année de la date de préfacturation (FR-185).
   */
  async getPrefacturations(annee?: number): Promise<PrefacturationResume[]> {
    const headers = await this.apiTokenService.getHeader();

    let params = new HttpParams();
    if (annee != null) {
      params = params.set("annee", annee);
    }

    return await lastValueFrom(
      this.http.get<PrefacturationResume[]>(`${this.newAPI}prefacturations`, {
        headers,
        params,
      })
    );
  }

  /**
   * Génère une préfacturation à partir d'un payload mixte missions / préfacs
   * complémentaires (l'API relaie vers le plugin CRM).
   */
  async genererPrefacturation(
    elements: ElementAPrefacturer[]
  ): Promise<ResultatPrefacturation> {
    const headers = await this.apiTokenService.getHeader();
    return await lastValueFrom(
      this.http.post<ResultatPrefacturation>(
        `${this.newAPI}prefacturations/generer`,
        elements,
        { headers }
      )
    );
  }

  /**
   * Document HTML d'une préfacture (blob privé servi tel quel par l'API) —
   * rendu et export PDF assurés côté app (WebView / impression natives).
   */
  async getPrefacturationDocument(prefacturationId: string): Promise<string> {
    const headers = await this.apiTokenService.getHeader();
    return await lastValueFrom(
      this.http.get(
        `${this.newAPI}prefacturations/${prefacturationId}/document`,
        { headers, responseType: "text" }
      )
    );
  }
}
